import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Building2, Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api, errorMessage } from "@/lib/api";
import { PortalLinkSheet } from "@/components/PortalLinkSheet";
import { PER_PAGE, Pager } from "@/components/Pager";
import { useAuth } from "@/context/AuthContext";

const BLANK = { name: "", address: "", tax_id: "", email: "", phone: "", city: "", notes: "" };

export default function Firms() {
  const [firms, setFirms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const { user } = useAuth();
  const isOwner = user?.role !== "clerk";
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get("q") || "");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/firms");
      setFirms(data);
    } catch (error) {
      toast.error(errorMessage(error, "Could not load law firms"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm(BLANK);
    setOpen(true);
  };

  const openEdit = (firm) => {
    setEditing(firm);
    setForm({ ...BLANK, ...firm });
    setOpen(true);
  };

  const save = async (event) => {
    event.preventDefault();
    if (!form.name.trim()) {
      toast.error("Firm name is required");
      return;
    }
    setSaving(true);
    const payload = {
      name: form.name,
      address: form.address,
      tax_id: form.tax_id,
      email: form.email,
      phone: form.phone,
      city: form.city,
      notes: form.notes,
    };
    try {
      if (editing) await api.put(`/firms/${editing.firm_id}`, payload);
      else await api.post("/firms", payload);
      toast.success(editing ? "Firm updated" : "Firm added");
      setOpen(false);
      load();
    } catch (error) {
      toast.error(errorMessage(error, "Could not save firm"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (firm) => {
    if (!window.confirm(`Delete ${firm.name}?`)) return;
    try {
      await api.delete(`/firms/${firm.firm_id}`);
      toast.success("Firm deleted");
      load();
    } catch (error) {
      toast.error(errorMessage(error, "Could not delete firm"));
    }
  };

  const field = (key) => ({
    value: form[key] ?? "",
    onChange: (e) => setForm((f) => ({ ...f, [key]: e.target.value })),
  });

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return firms;
    return firms.filter((f) =>
      [f.name, f.city, f.email]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(needle)),
    );
  }, [firms, query]);

  useEffect(() => {
    setPage(1);
  }, [query]);

  useEffect(() => {
    setQuery(searchParams.get("q") || "");
  }, [searchParams]);

  return (
    <div className="space-y-8" data-testid="firms-page">
      <header className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="label-caps">Billing parties</p>
          <h1 className="mt-3 font-serif text-3xl tracking-tight text-slate-900 lg:text-4xl">
            Law firms
          </h1>
          <p className="mt-3 max-w-xl text-sm text-slate-600">
            Parent accounts for the instructing lawyers who brief you. Firm-level records stay
            intact even when invoices are addressed to an individual solicitor.
          </p>
        </div>
        <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search law firms…"
          className="w-full rounded-sm sm:w-64"
          data-testid="firms-search"
        />
        <button
          type="button"
          onClick={openNew}
          data-testid="add-firm-button"
          className="inline-flex items-center gap-2 bg-slate-900 px-5 py-2.5 text-[13px] font-semibold text-white transition-colors duration-150 hover:bg-slate-800"
        >
          <Plus className="h-4 w-4" strokeWidth={2} />
          Add law firm
        </button>
        </div>
      </header>

      {loading ? (
        <div className="flex h-40 items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
        </div>
      ) : firms.length === 0 ? (
        <div className="surface p-10 text-center" data-testid="firms-empty">
          <Building2 className="mx-auto h-6 w-6 text-slate-300" strokeWidth={1.4} />
          <p className="mt-4 text-sm text-slate-600">
            No law firms on record. Add the firms that instruct you.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="firms-list">
          {filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE).map((firm) => (
            <article
              key={firm.firm_id}
              className="surface flex flex-col p-6"
              data-testid={`firm-card-${firm.firm_id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="font-serif text-lg leading-snug text-slate-900">{firm.name}</h2>
                  <p className="mt-1 text-[11px] uppercase tracking-[0.14em] text-slate-400">
                    {firm.city || "—"} · {firm.contact_count} lawyer(s)
                  </p>
                </div>
                <div className="flex gap-1">
                  <PortalLinkSheet scope="firm" id={firm.firm_id} name={firm.name} />
                  <button
                    type="button"
                    onClick={() => openEdit(firm)}
                    data-testid={`edit-firm-${firm.firm_id}`}
                    className="flex h-8 w-8 items-center justify-center border border-slate-200 text-slate-500 transition-colors duration-150 hover:border-slate-400 hover:text-slate-900"
                  >
                    <Pencil className="h-3.5 w-3.5" strokeWidth={1.6} />
                  </button>
                  {isOwner && (
                  <button
                    type="button"
                    onClick={() => remove(firm)}
                    data-testid={`delete-firm-${firm.firm_id}`}
                    className="flex h-8 w-8 items-center justify-center border border-slate-200 text-slate-500 transition-colors duration-150 hover:border-red-300 hover:text-red-600"
                  >
                    <Trash2 className="h-3.5 w-3.5" strokeWidth={1.6} />
                  </button>
                  )}
                </div>
              </div>
              <dl className="mt-5 space-y-2 border-t border-slate-100 pt-4 text-[12px] text-slate-600">
                {firm.address && <dd className="whitespace-pre-line">{firm.address}</dd>}
                {firm.email && <dd>{firm.email}</dd>}
                {firm.phone && <dd className="num">{firm.phone}</dd>}
                {firm.tax_id && <dd className="num text-slate-500">Tax ID {firm.tax_id}</dd>}
              </dl>
            </article>
          ))}
        </div>
        {filtered.length === 0 && (
          <p className="text-sm text-slate-500" data-testid="firms-no-match">
            No law firm matches &ldquo;{query.trim()}&rdquo;.
          </p>
        )}
        <Pager page={page} total={filtered.length} onPage={setPage} testId="firms-pager" />
        </div>
      )}

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-md">
          <SheetHeader>
            <SheetTitle className="font-serif text-xl">
              {editing ? "Edit law firm" : "Add law firm"}
            </SheetTitle>
            <SheetDescription className="text-[12px]">
              Firm-level accounting record. Instructing lawyers are linked separately.
            </SheetDescription>
          </SheetHeader>
          <form className="mt-7 space-y-5" onSubmit={save} data-testid="firm-form">
            <div className="space-y-2">
              <Label className="label-caps">Firm name *</Label>
              <Input {...field("name")} data-testid="firm-name-input" className="rounded-sm" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="label-caps">City</Label>
                <Input {...field("city")} data-testid="firm-city-input" className="rounded-sm" />
              </div>
              <div className="space-y-2">
                <Label className="label-caps">Tax / GST ID</Label>
                <Input {...field("tax_id")} data-testid="firm-tax-input" className="rounded-sm" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="label-caps">Main address</Label>
              <Textarea {...field("address")} rows={3} data-testid="firm-address-input" className="rounded-sm" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="label-caps">General email</Label>
                <Input {...field("email")} data-testid="firm-email-input" className="rounded-sm" />
              </div>
              <div className="space-y-2">
                <Label className="label-caps">Phone</Label>
                <Input {...field("phone")} data-testid="firm-phone-input" className="rounded-sm" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="label-caps">Notes</Label>
              <Textarea {...field("notes")} rows={2} data-testid="firm-notes-input" className="rounded-sm" />
            </div>
            <button
              type="submit"
              disabled={saving}
              data-testid="save-firm-button"
              className="inline-flex w-full items-center justify-center gap-2 bg-slate-900 px-5 py-3 text-[13px] font-semibold text-white transition-colors duration-150 hover:bg-slate-800 disabled:opacity-60"
            >
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {editing ? "Save changes" : "Add firm"}
            </button>
          </form>
        </SheetContent>
      </Sheet>
    </div>
  );
}
